import type { Coord, MapTheme } from "@dailywars/core/engine";
import { mapThemeConfig } from "@dailywars/core/render/mapThemes";
import type { ReactNode, Ref, RefObject } from "react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import type { BoardCursorMode } from "#/game/selection/types";

import { getCanvasCursor } from "./boardCursors";
import { handleBoardPointerDown, handleBoardPointerMove } from "./boardPointerHandlers";
import { MIN_BOARD_VIEWPORT_HEIGHT_PX, MIN_BOARD_VIEWPORT_PX } from "./boardViewport";

type GameCanvasLayoutProps = {
  viewportRef: RefObject<HTMLDivElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  cursorMode: BoardCursorMode;
  cssWidth: number;
  cssHeight: number;
  boardWidth: number;
  boardHeight: number;
  theme?: MapTheme;
  onTileSelect: (tile: Coord) => void;
  onTileHover: (tile: Coord | null) => void;
  onCancelSelection?: () => void;
  stickInspectOnClick?: boolean;
  viewportOverlay?: ReactNode;
};

type GameBoardCanvasSurfaceProps = {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  cursorMode: BoardCursorMode;
  cssWidth: number;
  cssHeight: number;
  boardWidth: number;
  boardHeight: number;
  theme?: MapTheme;
  onTileSelect: (tile: Coord) => void;
  onTileHover: (tile: Coord | null) => void;
  onCancelSelection?: () => void;
  stickInspectOnClick: boolean;
};

function useCanvasCursorRedraw({
  canvasRef,
  cursorMode,
}: {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  cursorMode: BoardCursorMode;
}) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const cursor = getCanvasCursor(cursorMode);
    canvas.style.cursor = "auto";
    canvas.style.cursor = cursor;
  }, [canvasRef, cursorMode]);
}

function canvasSurfaceStyle({
  theme,
  cursorMode,
  cssWidth,
  cssHeight,
}: {
  theme?: MapTheme;
  cursorMode: BoardCursorMode;
  cssWidth: number;
  cssHeight: number;
}) {
  return {
    backgroundColor: mapThemeConfig(theme).seaColors[0],
    cursor: getCanvasCursor(cursorMode),
    width: cssWidth,
    height: cssHeight,
  };
}

function GameBoardCanvasSurface(props: GameBoardCanvasSurfaceProps) {
  const {
    canvasRef,
    cursorMode,
    cssWidth,
    cssHeight,
    boardWidth,
    boardHeight,
    theme,
    onTileSelect,
    onTileHover,
    onCancelSelection,
    stickInspectOnClick,
  } = props;
  return (
    <canvas
      ref={canvasRef}
      className="mx-auto block max-w-none"
      style={canvasSurfaceStyle({ theme, cursorMode, cssWidth, cssHeight })}
      aria-label="Game board"
      onContextMenu={(event) => event.preventDefault()}
      onPointerDown={(event) =>
        handleBoardPointerDown({
          event,
          canvasRef,
          boardWidth,
          boardHeight,
          onTileSelect,
          onTileHover,
          onCancelSelection,
          stickInspectOnClick,
        })
      }
      onPointerMove={
        stickInspectOnClick
          ? undefined
          : (event) =>
              handleBoardPointerMove({
                event,
                canvasRef,
                boardWidth,
                boardHeight,
                onTileHover,
              })
      }
      onPointerLeave={stickInspectOnClick ? undefined : () => onTileHover(null)}
    />
  );
}

function ViewportOverlayPortal({
  overlayRootRef,
  viewportOverlay,
}: {
  overlayRootRef: Ref<HTMLDivElement | null>;
  viewportOverlay: ReactNode;
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (!mounted) return null;

  return createPortal(
    <div ref={overlayRootRef} className="pointer-events-none fixed inset-0 z-40 p-3">
      <div className="relative h-full min-h-0 w-full">{viewportOverlay}</div>
    </div>,
    document.body,
  );
}

export function GameCanvasLayout({
  overlayRootRef,
  viewportRef,
  canvasRef,
  cursorMode,
  cssWidth,
  cssHeight,
  boardWidth,
  boardHeight,
  theme,
  onTileSelect,
  onTileHover,
  onCancelSelection,
  stickInspectOnClick = false,
  viewportOverlay,
}: GameCanvasLayoutProps & { overlayRootRef: Ref<HTMLDivElement | null> }) {
  useCanvasCursorRedraw({ canvasRef, cursorMode });
  return (
    <div
      className="relative isolate w-full max-w-full"
      style={{ minWidth: MIN_BOARD_VIEWPORT_PX, minHeight: MIN_BOARD_VIEWPORT_HEIGHT_PX }}
    >
      <div
        ref={viewportRef}
        className="relative min-w-0 w-max max-w-full overflow-x-auto overflow-y-visible overscroll-x-contain ps-[max(0px,env(safe-area-inset-left))] pe-[max(0px,env(safe-area-inset-right))]"
      >
        <GameBoardCanvasSurface
          canvasRef={canvasRef}
          cursorMode={cursorMode}
          cssWidth={cssWidth}
          cssHeight={cssHeight}
          boardWidth={boardWidth}
          boardHeight={boardHeight}
          theme={theme}
          onTileSelect={onTileSelect}
          onTileHover={onTileHover}
          onCancelSelection={onCancelSelection}
          stickInspectOnClick={stickInspectOnClick}
        />
      </div>
      {viewportOverlay ? (
        <ViewportOverlayPortal overlayRootRef={overlayRootRef} viewportOverlay={viewportOverlay} />
      ) : null}
    </div>
  );
}
